import React, { useMemo } from 'react';
import {
  Box, Text, VStack, HStack,
} from '@gluestack-ui/themed';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import SaveStatusIndicator from '@/common/components/SaveStatusIndicator';
import SimpleRTE from '@/common/components/forms/SimpleRTE';
import ContentGroup from '@/common/components/ContentGroup';
import { useTenantGradingSettings } from '@/common/context/TenantSettingsStore';
import type { Task } from '@/common/api/models/task';
import type { EvaluationSaveStatus } from '@/lecturer/EvaluationDesk/hooks/useLecturerEvaluationController';
import {
  resolveOralExamDimensionKeys,
  resolveOralExamQuestionDisplay,
  resolveOralExamQuestionTranscriptHtml,
} from '@/lecturer/EvaluationDesk/utils/evaluationFormMapping';
import OralExamQuestionCard from './OralExamQuestionCard';

export interface OralExamEvaluationPanelProps {
  task: Task;
  taskId: string;
  isReadOnly: boolean;
  isDisabled: boolean;
  saveStatus: EvaluationSaveStatus;
  errorMessage: string | null;
  onRetry: () => void;
  isDirty: boolean;
  isAutograding?: boolean;
  isSaving?: boolean;
}

/**
 * OralExamEvaluationPanel
 * ----
 * Editable oral-exam evaluation with per-question ratings and overall summary.
 */
const OralExamEvaluationPanel: React.FC<OralExamEvaluationPanelProps> = ({
  task,
  taskId,
  isReadOnly,
  isDisabled,
  saveStatus,
  errorMessage,
  onRetry,
  isDirty,
  isAutograding = false,
  isSaving = false,
}) => {
  const { t } = useTranslation();
  const { oralExamDimensionKeys: tenantOralExamDimensionKeys } = useTenantGradingSettings();
  const oralExamDimensionKeys = useMemo(
    () => resolveOralExamDimensionKeys(task, tenantOralExamDimensionKeys),
    [task, tenantOralExamDimensionKeys],
  );
  const { control, getValues } = useFormContext();
  const subEvaluationsPath = `taskEvaluations.${taskId}.subEvaluations`;

  const subEvaluations = useWatch({
    control,
    name: subEvaluationsPath,
    defaultValue: {},
  }) as Record<string, unknown> | undefined;

  const promptVersion = useWatch({
    control,
    name: `taskEvaluations.${taskId}.promptVersion`,
  }) as string | null | undefined;

  const evalTs = useWatch({
    control,
    name: `taskEvaluations.${taskId}.evalTs`,
  }) as string | null | undefined;

  // Trust the form as the single source of truth for which questions to show.
  // buildTaskFormEvaluationFromApi lists submission.oralExamSession.selectedQuestionIds
  // in draw order (empty when the submission has no asked list); re-deriving here
  // would re-introduce key-namespace collisions (e.g. autograder "Q1" vs pool-prefix ids).
  const questionIds = useMemo(
    () => Object.keys(getValues(subEvaluationsPath) ?? subEvaluations ?? {}),
    [subEvaluations, getValues, subEvaluationsPath],
  );

  const getQuestionDisplay = (questionId: string) => (
    resolveOralExamQuestionDisplay(task, questionId, t)
  );

  return (
    <Box testID="oral-exam-evaluation-panel">
      <VStack space="md">
        {questionIds.map((questionId) => {
          const { headerTitle, fullQuestionText } = getQuestionDisplay(questionId);
          const transcriptHtml = resolveOralExamQuestionTranscriptHtml(task, questionId);
          return (
            <OralExamQuestionCard
              key={questionId}
              taskId={taskId}
              questionId={questionId}
              questionHeaderTitle={headerTitle}
              fullQuestionText={fullQuestionText}
              transcriptHtml={typeof transcriptHtml === 'string' ? transcriptHtml : undefined}
              oralExamDimensionKeys={oralExamDimensionKeys}
              isReadOnly={isReadOnly}
              isAutograding={isAutograding}
              isSaving={isSaving}
            />
          );
        })}

        <ContentGroup
          variant="bordered"
          bg="$bgCard"
          accentColor="$brandBase"
          accentWidth={3}
          p="$4"
        >
          <HStack alignItems="center" justifyContent="space-between" mb="$2">
            <Text size="xs" color="$textSubtle" fontWeight="medium">
              {t('lecturer.evalDesk.oralExam.summaryLabel')}
            </Text>
            <SaveStatusIndicator
              status={saveStatus}
              errorMessage={errorMessage}
              isDirty={isDirty}
              onRetry={onRetry}
            />
          </HStack>
          <SimpleRTE
            name={`taskEvaluations.${taskId}.overallFeedback.text`}
            placeholder={t('lecturer.evalDesk.oralExam.summaryLabel')}
            readonly={isReadOnly || isDisabled || isAutograding}
            minHeight={80}
            maxHeight={240}
          />
        </ContentGroup>

        {!!(promptVersion || evalTs) && (
          <HStack space="md" mt="$1">
            {!!promptVersion && (
              <Text size="xs" color="$textHint">
                {t('lecturer.evalDesk.oralExam.promptVersionLabel')}
                {': '}
                {promptVersion}
              </Text>
            )}
            {!!evalTs && (
              <Text size="xs" color="$textHint">
                {t('lecturer.evalDesk.oralExam.evaluatedAtLabel')}
                {': '}
                {new Date(evalTs).toLocaleString()}
              </Text>
            )}
          </HStack>
        )}
      </VStack>
    </Box>
  );
};

export default OralExamEvaluationPanel;
